<template>
  <Teleport to="body">
    <section v-if="visible" class="fixed inset-0 overlay p-10 z-[10000]">
      <div class="flex items-center gap-20">
        <div class="flex-1 flex gap-4 items-center">
          <span class="cursor-pointer" @click="close">
            <img src="@/assets/svg/prev-cancelCircle.svg" alt="" />
          </span>
          <div class="flex-1 py-2 px-3 rounded-lg">
            <span class="flex items-center gap-3">
              <img src="@/assets/svg/prevPlaceholder.svg" alt="" />
              <p
                class="mb-0 font-sfProDisplay font-medium text-xl leading-7 tracking-[0%] text-gray-500"
              >
                Images preview
              </p>
            </span>
          </div>
        </div>
        <div class="flex items-center gap-5">
          <span class="cursor-pointer" @click="zoomIn">
            <img src="@/assets/svg/prevPlus.svg" alt=""
          /></span>
          <span class="cursor-pointer" @click="zoomOut">
            <img src="@/assets/svg/prevMinus.svg" alt=""
          /></span>
          <span class="cursor-pointer" @click="downloadImage">
            <img src="@/assets/svg/prevExport.svg" alt=""
          /></span>
        </div>
      </div>

      <div class="flex flex-col h-[calc(100%-150px)] justify-center">
        <div
          ref="imageContainer"
          class="relative w-full h-full flex items-center justify-center overflow-hidden"
          @wheel="handleWheel"
        >
          <transition-group
            name="fade"
            tag="div"
            class="w-full h-full flex items-center justify-center"
          >
            <div
              v-for="(url, index) in images"
              :key="url"
              v-show="currentIndex === index"
              class="absolute inset-0 flex items-center justify-center"
            >
              <img
                :src="url"
                alt=""
                class="max-h-[500px] max-w-full object-contain"
                :style="{ transform: `scale(${zoomLevel})` }"
              />
            </div>
          </transition-group>

          <span
            @click="prevImage"
            class="cursor-pointer absolute left-4 flex items-center justify-center"
          >
            <img src="@/assets/svg/prevArrowRight.svg" alt="" />
          </span>

          <span
            @click="nextImage"
            class="cursor-pointer absolute right-4 flex items-center justify-center"
          >
            <img src="@/assets/svg/prevArrowLeft.svg" alt="" />
          </span>
        </div>
      </div>

      <div
        class="absolute bottom-0 w-full py-10 px-2 flex items-center justify-center"
      >
        <div class="flex gap-2 overflow-x-auto scrollbar-hide">
          <div
            v-for="(url, index) in images"
            :key="'thumb-' + index"
            @click="currentIndex = index"
            class="flex-shrink-0 w-20 h-20 overflow-hidden rounded cursor-pointer transition-all duration-200"
            :class="
              currentIndex === index ? 'border-2 border-estationsGreen-500' : ''
            "
          >
            <img :src="url" alt="" class="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  </Teleport>
</template>

<script>
export default {
  name: "ImageCarousel",
  emits: ["change", "close"],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array,
      default: () => [],
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      currentIndex: this.initialIndex,
      isTransitioning: false,
      zoomLevel: 1,
      maxZoom: 3,
      minZoom: 0.5,
      zoomStep: 0.2,
      lastWheelTime: 0,
      wheelThrottleDelay: 50,
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        window.addEventListener("keydown", this.handleKeydown);
        document.body.style.overflow = "hidden";
      } else {
        window.removeEventListener("keydown", this.handleKeydown);
        document.body.style.overflow = "auto";
        this.zoomLevel = 1;
      }
    },
    initialIndex(newVal) {
      this.currentIndex = newVal;
    },
    currentIndex() {
      this.zoomLevel = 1;
    },
  },
  methods: {
    close() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    nextImage() {
      if (this.isTransitioning) return;
      this.isTransitioning = true;

      if (this.currentIndex < this.images.length - 1) {
        this.currentIndex++;
      } else {
        this.currentIndex = 0;
      }

      this.$emit("change", this.currentIndex);

      setTimeout(() => {
        this.isTransitioning = false;
      }, 300);
    },
    prevImage() {
      if (this.isTransitioning) return;
      this.isTransitioning = true;

      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else {
        this.currentIndex = this.images.length - 1;
      }

      this.$emit("change", this.currentIndex);

      setTimeout(() => {
        this.isTransitioning = false;
      }, 300);
    },
    handleKeydown(e) {
      if (e.key === "ArrowRight") {
        this.nextImage();
      } else if (e.key === "ArrowLeft") {
        this.prevImage();
      } else if (e.key === "Escape") {
        this.close();
      } else if (e.key === "+") {
        this.zoomIn();
      } else if (e.key === "-") {
        this.zoomOut();
      }
    },
    zoomIn() {
      if (this.zoomLevel < this.maxZoom) {
        this.zoomLevel = Math.min(this.maxZoom, this.zoomLevel + this.zoomStep);
      }
    },
    zoomOut() {
      if (this.zoomLevel > this.minZoom) {
        this.zoomLevel = Math.max(this.minZoom, this.zoomLevel - this.zoomStep);
      }
    },
    handleWheel(e) {
      e.preventDefault();

      const now = Date.now();
      if (now - this.lastWheelTime < this.wheelThrottleDelay) {
        return;
      }
      this.lastWheelTime = now;
      const isPinchZoom = e.ctrlKey;

      if (isPinchZoom || Math.abs(e.deltaY) > 0) {
        const zoomDirection =
          e.deltaY < 0 || (isPinchZoom && e.deltaY < 0) ? 1 : -1;

        if (zoomDirection > 0) {
          this.zoomIn();
        } else {
          this.zoomOut();
        }
      }
    },
    async downloadImage() {
      if (!this.images.length || this.currentIndex >= this.images.length)
        return;
      try {
        const url = this.images[this.currentIndex];
        const link = document.createElement("a");
        link.href = url;
        link.download =
          url.split("/").pop() || `image-${this.currentIndex + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // const url = this.images[this.currentIndex];
        // const response = await fetch(url);
        // const blob = await response.blob();
        // const link = document.createElement('a');
        // link.href = URL.createObjectURL(blob);
        // const filename = url.split('/').pop() || `image-${this.currentIndex + 1}.jpg`;
        // link.download = filename;
        // document.body.appendChild(link);
        // link.click();
        // document.body.removeChild(link);
      } catch (error) {
        const msg = error;
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeydown);
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.overlay {
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(1px);
}

.zIndexWrapper {
  z-index: 1000 !important;
}
</style>
